import type { ModelDefinition } from "@/models.js";

/**
 * Built-in Grok Voice voices (GET /v1/tts/voices). The first entry ("ara") is
 * the upstream default when the caller omits a voice.
 */
const GROK_VOICE_VOICES = [
	"ara",
	"altair",
	"atlas",
	"aurora",
	"carina",
	"castor",
	"celeste",
	"cosmo",
	"eve",
	"helios",
	"helix",
	"iris",
	"kepler",
	"leo",
	"liora",
	"lumen",
	"luna",
	"lux",
	"naksh",
	"orion",
	"perseus",
	"rex",
	"rigel",
	"sal",
	"sirius",
	"ursa",
	"zagan",
	"zenith",
];

const xaiSupportedParamsNoFreqPresence = [
	"temperature",
	"max_tokens",
	"top_p",
	"response_format",
	"tools",
	"tool_choice",
];

export const xaiModels = [
	{
		id: "grok-3",
		name: "Grok-3",
		description: "xAI's advanced model with strong reasoning capabilities.",
		family: "xai",
		releasedAt: new Date("2025-02-17"),
		providers: [
			{
				test: "skip",
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-3",
				inputPrice: "3.0e-6",
				cachedInputPrice: "0.75e-6",
				outputPrice: "15.0e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: undefined,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2026-05-15"),
			},
		],
	},
	{
		id: "grok-3-mini",
		name: "Grok-3 Mini",
		description: "Compact Grok-3 for fast, cost-effective inference.",
		family: "xai",
		releasedAt: new Date("2025-04-09"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-3-mini",
				inputPrice: "0.3e-6",
				outputPrice: "0.5e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: undefined,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deprecatedAt: new Date("2026-01-30"),
				deactivatedAt: new Date("2026-02-28"),
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-3-fast",
		name: "Grok-3 Fast",
		description: "Optimized Grok-3 variant for lower latency.",
		family: "xai",
		releasedAt: new Date("2025-05-22"),
		providers: [
			{
				test: "skip",
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-3-fast",
				inputPrice: "5.0e-6",
				outputPrice: "25.0e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: undefined,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deprecatedAt: new Date("2025-08-08"),
				deactivatedAt: new Date("2025-09-15"),
			},
		],
	},
	{
		id: "grok-3-mini-fast",
		name: "Grok-3 Mini Fast",
		description: "Fastest Grok-3 Mini variant.",
		family: "xai",
		releasedAt: new Date("2025-02-17"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-3-mini-fast",
				inputPrice: "0.6e-6",
				outputPrice: "4.0e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: undefined,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deprecatedAt: new Date("2025-08-08"),
				deactivatedAt: new Date("2025-09-15"),
			},
		],
	},
	{
		id: "grok-2-1212",
		name: "Grok-2 (1212)",
		description: "December 2024 version of Grok-2.",
		family: "xai",
		releasedAt: new Date("2024-12-12"),
		providers: [
			{
				test: "skip",
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-2-1212",
				inputPrice: "2.0e-6",
				outputPrice: "10.0e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: undefined,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deprecatedAt: new Date("2025-08-08"),
				deactivatedAt: new Date("2025-09-15"),
			},
		],
	},
	{
		id: "grok-2-vision-1212",
		name: "Grok-2 Vision (1212)",
		description: "Grok-2 with vision capabilities.",
		family: "xai",
		releasedAt: new Date("2024-12-14"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-2-vision-1212",
				inputPrice: "2.0e-6",
				outputPrice: "10.0e-6",
				requestPrice: "0",
				imageInputPrice: "2.0e-6",
				contextSize: 32768,
				maxOutput: undefined,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				deprecatedAt: new Date("2025-08-08"),
				deactivatedAt: new Date("2025-09-15"),
			},
		],
	},
	{
		id: "grok-4-0709",
		name: "Grok 4 (0709)",
		description: "July 2025 version of Grok 4.",
		family: "xai",
		releasedAt: new Date("2025-07-09"),
		providers: [
			{
				test: "skip",
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4-0709",
				deactivatedAt: new Date("2026-05-15"),
				inputPrice: "3.0e-6",
				outputPrice: "15.0e-6",
				pricingTiers: [
					{
						name: "Up to 128K",
						upToTokens: 128000,
						inputPrice: "3.0e-6",
						outputPrice: "15.0e-6",
						cachedInputPrice: "0.75e-6",
					},
					{
						name: "Over 128K",
						upToTokens: Infinity,
						inputPrice: "6.0e-6",
						outputPrice: "30.0e-6",
						cachedInputPrice: "0",
					},
				],
				cachedInputPrice: "0.75e-6",
				requestPrice: "0",
				imageInputPrice: undefined,
				contextSize: 256000,
				maxOutput: 256000,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-4",
		name: "Grok 4",
		description: "Latest Grok 4 with vision and tool support.",
		family: "xai",
		releasedAt: new Date("2025-07-09"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4",
				inputPrice: "3.0e-6",
				outputPrice: "15.0e-6",
				pricingTiers: [
					{
						name: "Up to 128K",
						upToTokens: 128000,
						inputPrice: "3.0e-6",
						outputPrice: "15.0e-6",
						cachedInputPrice: "0.75e-6",
					},
					{
						name: "Over 128K",
						upToTokens: Infinity,
						inputPrice: "6.0e-6",
						outputPrice: "30.0e-6",
						cachedInputPrice: "0",
					},
				],
				cachedInputPrice: "0.75e-6",
				requestPrice: "0",
				imageInputPrice: undefined,
				contextSize: 256000,
				maxOutput: 256000,
				streaming: true,
				vision: true,
				// grok-4 always reasons and streams reasoning_content deltas;
				// without this flag the gateway folds them into content
				reasoning: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
			},
		],
	},
	{
		id: "grok-4-fast-reasoning",
		name: "Grok 4 Fast Reasoning",
		description: "Grok 4 optimized for fast reasoning.",
		family: "xai",
		releasedAt: new Date("2025-07-09"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4-fast-reasoning",
				deactivatedAt: new Date("2026-05-15"),
				inputPrice: "0.2e-6",
				outputPrice: "0.5e-6",
				pricingTiers: [
					{
						name: "Up to 128K",
						upToTokens: 128000,
						inputPrice: "0.2e-6",
						outputPrice: "0.5e-6",
						cachedInputPrice: "0.05e-6",
					},
					{
						name: "Over 128K",
						upToTokens: Infinity,
						inputPrice: "0.4e-6",
						outputPrice: "1.0e-6",
						cachedInputPrice: "0",
					},
				],
				cachedInputPrice: "0.05e-6",
				requestPrice: "0",
				imageInputPrice: undefined,
				contextSize: 2_000_000,
				maxOutput: 30000,
				streaming: true,
				vision: true,
				reasoning: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-4-fast-non-reasoning",
		name: "Grok 4 Fast Non-Reasoning",
		description: "Grok 4 without reasoning for faster responses.",
		family: "xai",
		releasedAt: new Date("2025-10-10"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4-fast-non-reasoning",
				deactivatedAt: new Date("2026-05-15"),
				inputPrice: "0.2e-6",
				outputPrice: "0.5e-6",
				pricingTiers: [
					{
						name: "Up to 128K",
						upToTokens: 128000,
						inputPrice: "0.2e-6",
						outputPrice: "0.5e-6",
						cachedInputPrice: "0.05e-6",
					},
					{
						name: "Over 128K",
						upToTokens: Infinity,
						inputPrice: "0.4e-6",
						outputPrice: "1.0e-6",
						cachedInputPrice: "0",
					},
				],
				cachedInputPrice: "0.05e-6",
				requestPrice: "0",
				imageInputPrice: undefined,
				contextSize: 2_000_000,
				maxOutput: 30000,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-code-fast-1",
		name: "Grok Code Fast 1",
		description: "Grok optimized for code generation tasks.",
		family: "xai",
		releasedAt: new Date("2025-08-28"),
		providers: [
			{
				test: "skip",
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-code-fast-1",
				deactivatedAt: new Date("2026-05-15"),
				inputPrice: "0.2e-6",
				cachedInputPrice: "0.02e-6",
				outputPrice: "1.5e-6",
				requestPrice: "0",
				contextSize: 256000,
				maxOutput: 10000,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-4-1-fast-reasoning",
		name: "Grok 4.1 Fast Reasoning",
		description: "Grok 4.1 with fast reasoning and vision support.",
		family: "xai",
		releasedAt: new Date("2025-11-19"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4-1-fast-reasoning",
				deactivatedAt: new Date("2026-05-15"),
				inputPrice: "0.2e-6",
				outputPrice: "0.5e-6",
				pricingTiers: [
					{
						name: "Up to 128K",
						upToTokens: 128000,
						inputPrice: "0.2e-6",
						outputPrice: "0.5e-6",
						cachedInputPrice: "0.05e-6",
					},
					{
						name: "Over 128K",
						upToTokens: Infinity,
						inputPrice: "0.4e-6",
						outputPrice: "1.0e-6",
						cachedInputPrice: "0",
					},
				],
				cachedInputPrice: "0.05e-6",
				requestPrice: "0",
				imageInputPrice: undefined,
				contextSize: 2_000_000,
				maxOutput: 30000,
				streaming: true,
				vision: true,
				reasoning: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
			{
				providerId: "azure-ai-foundry",
				externalId: "grok-4-1-fast-reasoning",
				inputPrice: "0.2e-6",
				outputPrice: "0.5e-6",
				requestPrice: "0",
				imageInputPrice: undefined,
				contextSize: 2_000_000,
				maxOutput: 30000,
				streaming: true,
				vision: true,
				reasoning: true,
				reasoningOutput: "omit",
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
			},
		],
	},
	{
		id: "grok-4-1-fast-non-reasoning",
		name: "Grok 4.1 Fast Non-Reasoning",
		description: "Grok 4.1 without reasoning mode.",
		family: "xai",
		releasedAt: new Date("2025-11-19"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4-1-fast-non-reasoning",
				deactivatedAt: new Date("2026-05-15"),
				inputPrice: "0.2e-6",
				outputPrice: "0.5e-6",
				pricingTiers: [
					{
						name: "Up to 128K",
						upToTokens: 128000,
						inputPrice: "0.2e-6",
						outputPrice: "0.5e-6",
						cachedInputPrice: "0.05e-6",
					},
					{
						name: "Over 128K",
						upToTokens: Infinity,
						inputPrice: "0.4e-6",
						outputPrice: "1.0e-6",
						cachedInputPrice: "0",
					},
				],
				cachedInputPrice: "0.05e-6",
				requestPrice: "0",
				imageInputPrice: undefined,
				contextSize: 2_000_000,
				maxOutput: 30000,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
			{
				providerId: "azure-ai-foundry",
				externalId: "grok-4-1-fast-non-reasoning",
				inputPrice: "0.2e-6",
				outputPrice: "0.5e-6",
				requestPrice: "0",
				imageInputPrice: undefined,
				contextSize: 2_000_000,
				maxOutput: 30000,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-4-20-multi-agent-beta-0309",
		name: "Grok 4.20 Multi-Agent Beta (0309)",
		description:
			"Grok 4.20 multi-agent model with reasoning, vision, and tool support.",
		family: "xai",
		releasedAt: new Date("2026-03-09"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4.20-multi-agent-beta-0309",
				// xAI multi-agent models do not work with the Chat Completions API.
				// They require the Responses API (/v1/responses) with orchestrated sub-agents.
				// Deactivated until the gateway adds xAI Responses API routing support.
				deactivatedAt: new Date("2026-03-27"),
				inputPrice: "2.0e-6",
				outputPrice: "6.0e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "2.0e-6",
						outputPrice: "6.0e-6",
						cachedInputPrice: "0.2e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "4.0e-6",
						outputPrice: "12.0e-6",
						cachedInputPrice: "0.4e-6",
					},
				],
				cachedInputPrice: "0.2e-6",
				requestPrice: "0",
				contextSize: 2_000_000,
				maxOutput: 30000,
				streaming: true,
				vision: true,
				reasoning: true,
				reasoningOutput: "omit",
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-4-20-beta-0309-reasoning",
		name: "Grok 4.20 Beta Reasoning (0309)",
		description: "Grok 4.20 reasoning model with vision and tool support.",
		family: "xai",
		releasedAt: new Date("2026-03-09"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4.20-beta-0309-reasoning",
				inputPrice: "2.0e-6",
				outputPrice: "6.0e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "2.0e-6",
						outputPrice: "6.0e-6",
						cachedInputPrice: "0.2e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "4.0e-6",
						outputPrice: "12.0e-6",
						cachedInputPrice: "0.4e-6",
					},
				],
				cachedInputPrice: "0.2e-6",
				requestPrice: "0",
				contextSize: 2_000_000,
				maxOutput: 30000,
				streaming: true,
				vision: true,
				reasoning: true,
				tools: true,
				jsonOutput: true,
				// Always reasons, but rejects the effort knob: xAI answers
				// `reasoning_effort` with 400 "does not support parameter
				// reasoningEffort", so the omission here is deliberate and
				// makes the gateway drop the value instead of forwarding it.
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-4-20-beta-0309-non-reasoning",
		name: "Grok 4.20 Beta Non-Reasoning (0309)",
		description: "Grok 4.20 non-reasoning model with vision and tool support.",
		family: "xai",
		releasedAt: new Date("2026-03-09"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4.20-beta-0309-non-reasoning",
				inputPrice: "2.0e-6",
				outputPrice: "6.0e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "2.0e-6",
						outputPrice: "6.0e-6",
						cachedInputPrice: "0.2e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "4.0e-6",
						outputPrice: "12.0e-6",
						cachedInputPrice: "0.4e-6",
					},
				],
				cachedInputPrice: "0.2e-6",
				requestPrice: "0",
				contextSize: 2_000_000,
				maxOutput: 30000,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-4-20-reasoning",
		name: "Grok 4.20 Reasoning",
		description: "Grok 4.20 reasoning model with vision and tool support.",
		family: "xai",
		releasedAt: new Date("2026-03-09"),
		providers: [
			{
				providerId: "vertex-openai",
				stability: "unstable" as const,
				externalId: "grok-4.20-reasoning",
				inputPrice: "1.25e-6",
				outputPrice: "2.5e-6",
				cachedInputPrice: "0.2e-6",
				requestPrice: "0",
				contextSize: 2_000_000,
				maxOutput: 30000,
				streaming: true,
				vision: true,
				reasoning: true,
				reasoningOutput: "omit",
				tools: true,
				jsonOutput: true,
				// Same upstream model as xai/grok-4-20-beta-0309-reasoning,
				// which 400s on `reasoning_effort` — leaving it undeclared
				// keeps the gateway from forwarding a value Grok 4.20 rejects.
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-4-20-non-reasoning",
		name: "Grok 4.20 Non-Reasoning",
		description: "Grok 4.20 non-reasoning model with vision and tool support.",
		family: "xai",
		releasedAt: new Date("2026-03-09"),
		providers: [
			{
				providerId: "vertex-openai",
				stability: "unstable" as const,
				externalId: "grok-4.20-non-reasoning",
				inputPrice: "1.25e-6",
				outputPrice: "2.5e-6",
				cachedInputPrice: "0.2e-6",
				requestPrice: "0",
				contextSize: 2_000_000,
				maxOutput: 30000,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-4-3",
		name: "Grok 4.3",
		description:
			"xAI's flagship reasoning model with 1M context, vision, and tool support.",
		family: "xai",
		releasedAt: new Date("2026-04-30"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4.3",
				inputPrice: "1.25e-6",
				outputPrice: "2.5e-6",
				cachedInputPrice: "0.2e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "1.25e-6",
						outputPrice: "2.5e-6",
						cachedInputPrice: "0.2e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "2.5e-6",
						outputPrice: "5.0e-6",
						cachedInputPrice: "0",
					},
				],
				requestPrice: "0",
				contextSize: 1_000_000,
				maxOutput: undefined,
				streaming: true,
				vision: true,
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high"],
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
			},
			{
				providerId: "aws-bedrock",
				externalId: "xai.grok-4.3",
				apiFormat: "openai-chat-completions",
				inputPrice: "1.25e-6",
				cachedInputPrice: "0.20e-6",
				outputPrice: "2.50e-6",
				requestPrice: "0",
				contextSize: 1_000_000,
				maxOutput: 131_072,
				// Near the documented ceiling of 2 the deployment reasons without
				// ever finishing: 1.9 ran past 150s, 1.5 took 20s.
				maxTemperature: 1,
				streaming: true,
				vision: true,
				requiresBase64Images: true,
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high"],
				reasoningOutput: "omit",
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
				regions: [{ id: "global" }, { id: "us" }, { id: "us-west-2" }],
			},
			{
				providerId: "azure-ai-foundry",
				externalId: "grok-4.3",
				inputPrice: "1.25e-6",
				cachedInputPrice: "0.20e-6",
				outputPrice: "2.50e-6",
				requestPrice: "0",
				contextSize: 20_000,
				maxOutput: 8_192,
				streaming: true,
				vision: false,
				reasoning: true,
				reasoningOutput: "omit",
				tools: true,
				jsonOutput: false,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
			},
		],
	},
	{
		id: "grok-imagine-image-pro",
		name: "Grok Imagine Image Pro",
		description:
			"xAI's premium image generation model with image-to-image support.",
		family: "xai",
		output: ["image"],
		releasedAt: new Date("2026-03-02"),
		providers: [
			{
				test: "skip",
				providerId: "xai",
				externalId: "grok-imagine-image-pro",
				deactivatedAt: new Date("2026-05-15"),
				inputPrice: "0",
				outputPrice: "0",
				requestPrice: "0.07",
				imageInputPrice: "0.002",
				contextSize: 2000,
				maxOutput: 4096,
				streaming: false,
				vision: true,
				tools: false,
				jsonOutput: false,
				imageGenerations: true,
			},
		],
	},
	{
		id: "grok-imagine-image",
		name: "Grok Imagine Image",
		description:
			"xAI's fast image generation model with image-to-image support.",
		family: "xai",
		output: ["image"],
		releasedAt: new Date("2026-03-02"),
		providers: [
			{
				test: "skip",
				providerId: "xai",
				externalId: "grok-imagine-image",
				inputPrice: "0",
				outputPrice: "0",
				requestPrice: "0.02",
				imageInputPrice: "0.002",
				contextSize: 2000,
				maxOutput: 4096,
				streaming: false,
				vision: true,
				tools: false,
				jsonOutput: false,
				imageGenerations: true,
			},
		],
	},
	{
		id: "grok-imagine-image-2-0",
		name: "Grok Imagine Image 2.0",
		description:
			"xAI's second-generation image model for text-to-image generation and reference-image editing, with low and medium quality modes at 1k or 2k resolution.",
		family: "xai",
		output: ["image"],
		releasedAt: new Date("2026-08-08"),
		providers: [
			{
				test: "skip",
				providerId: "xai",
				externalId: "grok-imagine-image-2.0",
				inputPrice: "0",
				outputPrice: "0",
				// Billed per generated image at a rate that varies with both the
				// requested quality and resolution, so the tier keys are
				// "<quality>/<resolution>". xAI serves medium quality at 1k when the
				// request omits either knob, which is what "default" covers.
				perImagePrice: {
					"low/1k": "0.04",
					"low/2k": "0.06",
					"medium/1k": "0.06",
					"medium/2k": "0.08",
					default: "0.06",
				},
				contextSize: 2000,
				maxOutput: 4096,
				streaming: false,
				vision: true,
				tools: false,
				jsonOutput: false,
				imageGenerations: true,
			},
		],
	},
	{
		id: "grok-imagine-video-1-5-lite",
		name: "Grok Imagine Video 1.5 Lite",
		description:
			"xAI's lightweight video generation model. Creates videos from a text prompt or an input image at a low per-second price. Served from us-east-1 and us-west-2.",
		family: "xai",
		output: ["video"],
		releasedAt: new Date("2026-10-08"),
		providers: [
			{
				test: "skip",
				providerId: "xai",
				externalId: "grok-imagine-video-1.5-lite",
				inputPrice: "0",
				outputPrice: "0",
				requestPrice: "0",
				imageInputPrice: "0.01",
				perSecondPrice: {
					"480p": "0.02",
					"720p": "0.03",
					default: "0.03",
				},
				contextSize: 2000,
				maxOutput: 1,
				streaming: false,
				vision: true,
				tools: false,
				jsonOutput: false,
				videoGenerations: true,
				supportedVideoSizes: ["848x480", "1280x720"],
				supportedVideoDurationsSeconds: [6, 8, 10, 12, 15],
				supportsVideoAudio: true,
				supportsVideoWithoutAudio: false,
			},
		],
	},
	{
		id: "grok-imagine-video-1-5",
		name: "Grok Imagine Video 1.5",
		description:
			"xAI's image-to-video generation model. Requires an input image. Creates videos up to 15 seconds at 480p or 720p.",
		imageInputRequired: true,
		family: "xai",
		output: ["video"],
		releasedAt: new Date("2026-06-17"),
		providers: [
			{
				test: "skip",
				providerId: "xai",
				externalId: "grok-imagine-video-1.5",
				inputPrice: "0",
				outputPrice: "0",
				requestPrice: "0",
				imageInputPrice: "0.01",
				perSecondPrice: {
					"480p": "0.08",
					"720p": "0.14",
					default: "0.14",
				},
				contextSize: 2000,
				maxOutput: 1,
				streaming: false,
				vision: true,
				tools: false,
				jsonOutput: false,
				videoGenerations: true,
				supportedVideoSizes: ["848x480", "1280x720"],
				supportedVideoDurationsSeconds: [6, 8, 10, 12, 15],
				supportsVideoAudio: true,
				supportsVideoWithoutAudio: false,
			},
		],
	},
	{
		id: "grok-imagine-video-1-5-preview",
		name: "Grok Imagine Video 1.5 Preview",
		description:
			"xAI's image-to-video generation model. Requires an input image. Creates videos up to 15 seconds at 480p or 720p.",
		imageInputRequired: true,
		family: "xai",
		output: ["video"],
		releasedAt: new Date("2026-05-30"),
		providers: [
			{
				test: "skip",
				providerId: "xai",
				externalId: "grok-imagine-video-1.5-preview",
				inputPrice: "0",
				outputPrice: "0",
				requestPrice: "0",
				imageInputPrice: "0.01",
				perSecondPrice: {
					"480p": "0.08",
					"720p": "0.14",
					default: "0.14",
				},
				contextSize: 2000,
				maxOutput: 1,
				streaming: false,
				vision: true,
				tools: false,
				jsonOutput: false,
				videoGenerations: true,
				supportedVideoSizes: ["848x480", "1280x720"],
				supportedVideoDurationsSeconds: [6, 8, 10, 12, 15],
				supportsVideoAudio: true,
				supportsVideoWithoutAudio: false,
			},
		],
	},
	{
		id: "grok-4-5",
		name: "Grok 4.5",
		description:
			"xAI's flagship reasoning model with a 500K context window, vision, and tool support.",
		aliases: ["grok-4.5-latest", "grok-build-latest"],
		family: "xai",
		releasedAt: new Date("2026-07-08"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4.5",
				inputPrice: "2.0e-6",
				cachedInputPrice: "0.3e-6",
				outputPrice: "6.0e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "2.0e-6",
						outputPrice: "6.0e-6",
						cachedInputPrice: "0.3e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "4.0e-6",
						outputPrice: "12.0e-6",
						cachedInputPrice: "0.6e-6",
					},
				],
				requestPrice: "0",
				contextSize: 500_000,
				maxOutput: undefined,
				streaming: true,
				vision: true,
				reasoning: true,
				// xAI normalizes `minimal` to low and documents `xhigh` as high for
				// Grok 4.5. Only expose the distinct reasoning controls.
				reasoningEfforts: ["low", "medium", "high"],
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
			},
		],
	},
	{
		id: "grok-4-6",
		name: "Grok 4.6",
		description:
			"xAI's flagship reasoning model with frontier coding, knowledge work, and STEM performance, a 500K context window, vision, and tool support.",
		family: "xai",
		releasedAt: new Date("2026-08-06"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4.6",
				inputPrice: "2.0e-6",
				cachedInputPrice: "0.5e-6",
				outputPrice: "6.0e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "2.0e-6",
						outputPrice: "6.0e-6",
						cachedInputPrice: "0.5e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "4.0e-6",
						outputPrice: "12.0e-6",
						cachedInputPrice: "1.0e-6",
					},
				],
				requestPrice: "0",
				contextSize: 500_000,
				maxOutput: undefined,
				streaming: true,
				vision: true,
				reasoning: true,
				// Reasons on every request: xAI normalizes `minimal` to low and rejects
				// both `none` and `max`, so only distinct tiers are exposed.
				reasoningEfforts: ["low", "medium", "high", "xhigh"],
				tools: true,
				jsonOutput: true,
				// `stop`, `frequency_penalty` and `presence_penalty` all 400 with
				// "does not support parameter", so they stay undeclared.
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
			},
			{
				providerId: "aws-bedrock",
				externalId: "xai.grok-4.6",
				apiFormat: "openai-chat-completions",
				crossRegionProfiles: true,
				inputPrice: "2.0e-6",
				cachedInputPrice: "0.5e-6",
				outputPrice: "6.0e-6",
				requestPrice: "0",
				contextSize: 500_000,
				maxOutput: undefined,
				// Near the documented ceiling of 2 the deployment reasons without
				// ever finishing: 1.9 ran past 150s, 1.5 took 20s.
				maxTemperature: 1,
				streaming: true,
				vision: true,
				requiresBase64Images: true,
				reasoning: true,
				reasoningEfforts: ["low", "medium", "high", "xhigh"],
				reasoningOutput: "omit",
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
				regions: [{ id: "global" }, { id: "us" }, { id: "us-west-2" }],
			},
			{
				providerId: "vertex-openai",
				externalId: "grok-4.6",
				inputPrice: "2.0e-6",
				cachedInputPrice: "0.5e-6",
				outputPrice: "6.0e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "2.0e-6",
						outputPrice: "6.0e-6",
						cachedInputPrice: "0.5e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "4.0e-6",
						outputPrice: "12.0e-6",
						cachedInputPrice: "1.0e-6",
					},
				],
				requestPrice: "0",
				contextSize: 500_000,
				maxOutput: undefined,
				streaming: true,
				vision: true,
				reasoning: true,
				reasoningEfforts: ["minimal", "low", "medium", "high"],
				reasoningOutput: "omit",
				tools: true,
				supportedToolChoices: ["auto", "none", "required"],
				jsonOutput: true,
				jsonOutputSchema: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
			},
		],
	},
	{
		id: "grok-4-7",
		name: "Grok 4.7",
		description:
			"xAI's flagship reasoning model on a new, larger base model with frontier coding, agentic, and knowledge-work performance, a 500K context window, vision, and tool support.",
		family: "xai",
		releasedAt: new Date("2026-09-21"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4.7",
				inputPrice: "2.0e-6",
				cachedInputPrice: "0.5e-6",
				outputPrice: "6.0e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "2.0e-6",
						outputPrice: "6.0e-6",
						cachedInputPrice: "0.5e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "4.0e-6",
						outputPrice: "12.0e-6",
						cachedInputPrice: "1.0e-6",
					},
				],
				requestPrice: "0",
				contextSize: 500_000,
				maxOutput: undefined,
				streaming: true,
				vision: true,
				reasoning: true,
				reasoningEfforts: ["low", "medium", "high", "xhigh"],
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
			},
			{
				providerId: "aws-bedrock",
				externalId: "xai.grok-4.7",
				apiFormat: "openai-chat-completions",
				crossRegionProfiles: true,
				inputPrice: "2.0e-6",
				cachedInputPrice: "0.5e-6",
				outputPrice: "6.0e-6",
				requestPrice: "0",
				contextSize: 524_288,
				maxOutput: undefined,
				// Near the documented ceiling of 2 the deployment reasons without
				// ever finishing: 1.9 ran past 150s, 1.5 took 20s.
				maxTemperature: 1,
				streaming: true,
				vision: true,
				requiresBase64Images: true,
				reasoning: true,
				reasoningEfforts: ["low", "medium", "high", "xhigh"],
				reasoningOutput: "omit",
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
				regions: [{ id: "global" }, { id: "us" }],
			},
		],
	},
	{
		id: "grok-build-0-1",
		name: "Grok Build 0.1",
		description:
			"xAI's fast coding model for agentic software engineering workflows.",
		family: "xai",
		releasedAt: new Date("2026-05-20"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-build-0.1",
				inputPrice: "1.0e-6",
				outputPrice: "2.0e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "1.0e-6",
						outputPrice: "2.0e-6",
						cachedInputPrice: "0.2e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "2.0e-6",
						outputPrice: "4.0e-6",
						cachedInputPrice: "0.4e-6",
					},
				],
				cachedInputPrice: "0.2e-6",
				requestPrice: "0",
				contextSize: 256000,
				maxOutput: 256000,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				reasoning: true,
				// Reasons by default but rejects the effort knob: xAI answers
				// `reasoning_effort` with 400 "does not support parameter
				// reasoningEffort", so it stays undeclared on purpose.
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-stt-1-0",
		name: "Grok STT 1.0",
		description:
			"xAI's speech-to-text model. Transcribes audio files into text with word-level timestamps, speaker diarization, multichannel transcription, and inverse text normalization across 25 languages via the /v1/audio/transcriptions endpoint.",
		family: "xai",
		output: ["transcription"],
		releasedAt: new Date("2026-07-23"),
		providers: [
			{
				providerId: "xai",
				externalId: "grok-stt-1.0",
				inputPrice: "0",
				outputPrice: "0",
				// Billed on input audio duration at $0.10 per hour, against the
				// `duration` (seconds) reported by the xAI /v1/stt response.
				inputAudioHourPrice: "0.10",
				requestPrice: "0",
				streaming: false,
				tools: false,
				jsonOutput: false,
				transcriptions: true,
			},
		],
	},
	{
		id: "grok-voice-3-0",
		name: "Grok Voice 3.0",
		description:
			"xAI's speech-to-speech voice agent model. Served via the gateway's /v1/realtime WebSocket endpoint with text and audio input/output and function calling.",
		family: "xai",
		output: ["text", "audio"],
		releasedAt: new Date("2026-10-14"),
		stability: "beta",
		providers: [
			{
				providerId: "xai",
				externalId: "grok-voice-3.0",
				// xAI bills by session time plus a flat fee per text
				// input, not by tokens; the token prices are zero so the reported
				// token counts are recorded without being billed.
				inputPrice: "0",
				cachedInputPrice: "0",
				outputPrice: "0",
				inputAudioPrice: "0",
				cachedInputAudioPrice: "0",
				outputAudioPrice: "0",
				inputAudioHourPrice: "4.80",
				inputTextItemPrice: "0.004",
				requestPrice: "0",
				streaming: false,
				audio: true,
				tools: true,
				jsonOutput: false,
				realtime: true,
				supportedVoices: GROK_VOICE_VOICES,
				test: "skip",
			},
		],
	},
] as const satisfies ModelDefinition[];
